import { toast } from "sonner";
import { create } from "zustand";
import * as api from "@/features/invoices/api";
import {
  calcDueDate,
  calcExtendedAmt,
  calcSubtotal,
  calcTaxAmt,
} from "@/features/invoices/lib/invoice-calc";
import type { ConfirmOptions, RefundFields } from "@/features/invoices/api";
import type {
  Invoice,
  InvoiceLineItem,
  InvoicePayment,
  InvoiceTerms,
  Payment,
  PaymentSource,
  PaymentType,
} from "@/features/invoices/types";

interface PaymentInput {
  paymentDate: string;
  paymentAmtCents: number;
  reference?: string;
  source: PaymentSource;
  note?: string;
  paymentType?: Exclude<PaymentType, "REFUND">;
}

/** Recomputes taxAmtCents for one invoice from its current line items. */
function recomputeTax(
  invoices: Invoice[],
  lineItems: InvoiceLineItem[],
  invoiceId: string,
): Invoice[] {
  return invoices.map((inv) => {
    if (inv.id !== invoiceId) return inv;
    const subtotalCents = calcSubtotal(lineItems.filter((li) => li.invoiceId === invoiceId));
    return { ...inv, taxAmtCents: calcTaxAmt(subtotalCents, inv.taxPercent) };
  });
}

// Debounced background persistence for per-keystroke field edits, keyed by
// entity id — the UI updates optimistically, the API is patched once typing
// settles, then the client's data is refetched to reconcile server-computed
// values. Module-level (not store state) so it survives re-renders.
const timers = new Map<string, ReturnType<typeof setTimeout>>();
function schedulePersist(key: string, fn: () => Promise<void>, delay = 450) {
  const existing = timers.get(key);
  if (existing) clearTimeout(existing);
  timers.set(
    key,
    setTimeout(() => {
      timers.delete(key);
      fn().catch(() => toast.error("Couldn't save changes."));
    }, delay),
  );
}

interface InvoiceState {
  clientId: string | null;
  loading: boolean;
  invoices: Invoice[];
  lineItems: InvoiceLineItem[];
  payments: Payment[];
  invoicePayments: InvoicePayment[];

  loadClient: (clientId: string) => Promise<void>;
  refresh: () => Promise<void>;

  createDraftInvoice: (clientId: string, defaults: { billTo: string; taxPercent: number; terms: InvoiceTerms }) => Promise<string>;
  updateInvoiceHeader: (
    invoiceId: string,
    patch: Partial<Pick<Invoice, "invoiceDate" | "terms" | "billTo" | "taxPercent" | "noteToClient">>,
  ) => void;
  confirmInvoice: (invoiceId: string, options?: ConfirmOptions) => Promise<void>;
  deleteDraftInvoice: (invoiceId: string) => Promise<void>;
  voidInvoice: (invoiceId: string) => Promise<void>;

  addLineItem: (invoiceId: string, item: { description: string; quantity: number; unitPriceCents: number }) => Promise<void>;
  updateLineItem: (
    itemId: string,
    patch: Partial<Pick<InvoiceLineItem, "description" | "quantity" | "unitPriceCents">>,
  ) => void;
  deleteLineItem: (itemId: string) => Promise<void>;

  recordPayment: (
    clientId: string,
    payment: PaymentInput,
    distribution: { invoiceId: string; applyCents: number }[],
  ) => Promise<void>;
  updatePayment: (
    paymentId: string,
    payment: PaymentInput,
    distribution: { invoiceId: string; applyCents: number }[],
  ) => Promise<void>;
  deletePayment: (paymentId: string) => Promise<void>;
  refundPayment: (paymentId: string, fields: RefundFields) => Promise<void>;
}

export const useInvoiceStore = create<InvoiceState>((set, get) => ({
  clientId: null,
  loading: false,
  invoices: [],
  lineItems: [],
  payments: [],
  invoicePayments: [],

  loadClient: async (clientId) => {
    set({ clientId, loading: true });
    try {
      const data = await api.fetchClientBilling(clientId);
      // Ignore a stale response if the active client changed mid-flight.
      if (get().clientId !== clientId) return;
      set({ ...data, loading: false });
    } catch {
      if (get().clientId !== clientId) return;
      set({ loading: false });
      toast.error("Couldn't load billing data.");
    }
  },

  refresh: async () => {
    const clientId = get().clientId;
    if (!clientId) return;
    const data = await api.fetchClientBilling(clientId);
    if (get().clientId !== clientId) return;
    set(data);
  },

  createDraftInvoice: async (clientId, defaults) => {
    const invoice = await api.createDraftInvoice(clientId, defaults);
    await get().refresh();
    return invoice.id;
  },

  updateInvoiceHeader: (invoiceId, patch) => {
    // Optimistic local update (mirrors the server's due-date/tax recompute).
    set((state) => {
      const invoices = state.invoices.map((inv) => {
        if (inv.id !== invoiceId || inv.status !== "DRAFT") return inv;
        const updated = { ...inv, ...patch };
        if (patch.invoiceDate !== undefined || patch.terms !== undefined) {
          updated.dueDate = calcDueDate(updated.invoiceDate, updated.terms);
        }
        if (patch.taxPercent !== undefined) {
          const subtotalCents = calcSubtotal(
            state.lineItems.filter((item) => item.invoiceId === invoiceId),
          );
          updated.taxAmtCents = calcTaxAmt(subtotalCents, patch.taxPercent);
        }
        return updated;
      });
      return { invoices };
    });
    // Persist the current full header (not just this patch) so rapid edits to
    // different fields under one debounce key don't drop the earlier field.
    schedulePersist(`invoice:${invoiceId}`, async () => {
      const current = get().invoices.find((inv) => inv.id === invoiceId);
      if (!current) return;
      await api.updateInvoiceHeader(invoiceId, {
        invoiceDate: current.invoiceDate,
        terms: current.terms,
        billTo: current.billTo,
        taxPercent: current.taxPercent,
        noteToClient: current.noteToClient,
      });
      await get().refresh();
    });
  },

  confirmInvoice: async (invoiceId, options) => {
    await api.confirmInvoice(invoiceId, options);
    await get().refresh();
  },

  deleteDraftInvoice: async (invoiceId) => {
    await api.deleteInvoice(invoiceId);
    await get().refresh();
  },

  voidInvoice: async (invoiceId) => {
    await api.voidInvoice(invoiceId);
    await get().refresh();
  },

  addLineItem: async (invoiceId, item) => {
    await api.addLineItem(invoiceId, item);
    await get().refresh();
  },

  updateLineItem: (itemId, patch) => {
    let invoiceId: string | undefined;
    set((state) => {
      const target = state.lineItems.find((item) => item.id === itemId);
      if (!target) return state;
      invoiceId = target.invoiceId;
      const lineItems = state.lineItems.map((item) => {
        if (item.id !== itemId) return item;
        const updated = { ...item, ...patch };
        updated.extendedAmtCents = calcExtendedAmt(updated.quantity, updated.unitPriceCents);
        return updated;
      });
      return { lineItems, invoices: recomputeTax(state.invoices, lineItems, target.invoiceId) };
    });
    if (!invoiceId) return;
    const parentId = invoiceId;
    // Persist the current full line item so rapid multi-field edits under one
    // debounce key don't drop an earlier field.
    schedulePersist(`lineitem:${itemId}`, async () => {
      const current = get().lineItems.find((item) => item.id === itemId);
      if (!current) return;
      await api.updateLineItem(parentId, itemId, {
        description: current.description,
        quantity: current.quantity,
        unitPriceCents: current.unitPriceCents,
      });
      await get().refresh();
    });
  },

  deleteLineItem: async (itemId) => {
    const target = get().lineItems.find((item) => item.id === itemId);
    if (!target) return;
    await api.deleteLineItem(target.invoiceId, itemId);
    await get().refresh();
  },

  recordPayment: async (clientId, payment, distribution) => {
    await api.recordPayment(clientId, payment, distribution);
    await get().refresh();
  },

  updatePayment: async (paymentId, payment, distribution) => {
    await api.updatePayment(paymentId, payment, distribution);
    await get().refresh();
  },

  deletePayment: async (paymentId) => {
    await api.deletePayment(paymentId);
    await get().refresh();
  },

  refundPayment: async (paymentId, fields) => {
    await api.refundPayment(paymentId, fields);
    await get().refresh();
  },
}));
